import {
  EngagementAccount,
  EngagementPosts,
  FollowersGrowth,
  FollowsUnfollows,
  Media,
  Post,
  PostsFrequency,
  Pricing,
  Profile,
  ProfileAdvancedInsights,
  SentimentPricing,
  SocialReportDetail,
  Stats,
  TopCities,
  TopCountriesLanguages,
  TopGenderAge,
  TopMentions,
  ValueDescriptionString,
  ValueDescriptionType,
  ValuePostsStory,
} from "core/types/social.types";

export const transformProfileList = (data: any): Profile[] =>
  data?.map((result: any) => transformProfile(result));

export const transformProfile = (data: any): Profile => ({
  id: data?.id,
  name: data?.name,
  username: data?.username,
  mediaCount: data?.media_count,
  followersCount: data?.followers_count,
  followsCount: data?.follows_count,
  profilePictureUrl: data?.profile_picture_url,
});

export const transformMediaList = (data: any): Media[] =>
  data?.map((result: any) => transformMedia(result));

export const transformMedia = (data: any): Media => ({
  id: data?.id,
  caption: data?.caption,
  commentsCount: data?.comments_count,
  likeCount: data?.like_count,
  mediaProductType: data?.media_product_type,
  mediaType: data?.media_type,
  mediaUrl: data?.media_url,
  permalink: data?.permalink,
  sentimentPricing: data?.sentiment_pricing?.map((sp: any) =>
    transformSentimentPricing(sp)
  ),
  timestamp: data?.timestamp,
});

export const transformSentimentPricing = (data: any): SentimentPricing => ({
  CoDeTokens: data?.CoDe_tokens,
  gptPricing: data?.gpt_pricing,
  infrastructurePricing: data?.infrastructure_pricing,
  level: data?.level,
  nlpTokens: data?.nlp_tokens,
  totalComments: data?.total_comments,
  totalCommentsAnalyzed: data?.total_comments_analyzed,
});

export const transformPricing = (data: any): Pricing => ({
  id: data?.id,
  followersCount: data?.followers_count,
  followsCount: data?.follows_count,
  mediaCount: data?.media_count,
  posts: transformMediaList(data?.posts),
  userTokens: data?.user_tokens,
  username: data?.username,
});

export const transformSocialReportDetail = (data: any): SocialReportDetail => ({
  userId: data?.user_id,
  accountInfo: transformProfile(data?.account_info),
  userLevel: data?.user_level,
  postsStatistics: transformPostsStatistics(data?.posts_statistics),
  posts: data?.posts.map((result: any) => tranformPost(result)),
  wordCloud: data?.word_cloud.map((word_cloud: any) => ({
    text: word_cloud.text,
    value: word_cloud.value,
  })),
  // followersAudience: transformFollowersAudience(data?.followers_audiance),
  profileAdvancedInsights: transformProfileAdvancedInsights(
    data?.profile_advanced_insights
  ),
  timestampAnalysis: data?.timestamp_analysis,
});

// export const transformFollowersAudience = (data: any): FollowersAudience => ({
//   age: data?.age,
//   city: data?.city,
//   country: data?.country,
//   gender: {
//     F: data?.gender["F"],
//     M: data?.gender["M"],
//     U: data?.gender["U"],
//   },
// });

export const tranformPost = (data: any): Post => ({
  positiveCount: data?.positive_count,
  neutralCount: data?.neutral_count,
  negativeCount: data?.negative_count,
  positiveRatio: data?.positive_ratio,
  positiveRatioFull: data?.positive_ratio_full,
  sentimentScale: data?.sentiment_scale,
  commentsCount: data?.comments_count,
  avgSentimentScore: data?.avg_sentiment_score,
  mostCommonEmotions:
    data?.most_common_emotions &&
    Object.keys(data?.most_common_emotions).length > 0
      ? data?.most_common_emotions
      : {},
  mostCommonIntents:
    data?.most_common_intents &&
    Object.keys(data?.most_common_intents).length > 0
      ? data?.most_common_intents
      : {},
  spamCommentsCount: data?.spam_comments_count,
  postId: data?.post_id,
  postInfo: transformMedia(data?.post_info),
});

export const transformPostsStatistics = (data: any): Stats => ({
  avgAllPostSentiment: data?.avg_all_post_sentiment,
  avgAllPostSentimentFull: data?.avg_all_post_sentiment_full,
  postWithCommentsCount: data?.post_with_comments_count,
});

export const transformProfileAdvancedInsights = (
  data: any
): ProfileAdvancedInsights => ({
  topCountriesLanguages:
    Object.keys(data?.top_countries_languages).length > 0
      ? transformTopCountriesLanguages(data?.top_countries_languages)
      : undefined,
  followersGrowth:
    Object.keys(data?.followers_growth).length > 0
      ? transformFollowersGrowth(data?.followers_growth)
      : undefined,
  topGenderAge:
    Object.keys(data?.top_gender_age).length > 0
      ? transformTopGenderAge(data?.top_gender_age)
      : undefined,
  topCities:
    Object.keys(data?.top_cities).length > 0
      ? transformTopCities(data?.top_cities)
      : undefined,
  engagementPosts:
    Object.keys(data?.engagement_posts).length > 0
      ? transformEngagementPosts(data?.engagement_posts)
      : undefined,
  engagementAccount:
    Object.keys(data?.engagement_account).length > 0
      ? transformEngagementAccount(data?.engagement_account)
      : undefined,
  postsFrequency:
    Object.keys(data?.posts_frequency).length > 0
      ? transformPostsFrequency(data?.posts_frequency)
      : undefined,
  valuePostStory:
    Object.keys(data?.value_post_story).length > 0
      ? transformValuePostStory(data?.value_post_story)
      : undefined,
  followsUnfollows:
    Object.keys(data?.follows_unfollows).length > 0
      ? transformFollowsUnfollows(data?.follows_unfollows)
      : undefined,
  topMentions: transformTopMentions(data?.top_mentions),
});

export const transformTopCountriesLanguages = (
  data: any
): TopCountriesLanguages => ({
  title: data?.title,
  description: data?.description,
  values: {
    topCountries: transformValueDescriptionType(data?.values?.top_countries),
    topLanguages: transformValueDescriptionType(data?.values?.top_languages),
  },
});

export const transformFollowersGrowth = (data: any): FollowersGrowth => ({
  title: data?.title,
  description: data?.description,
  values: {
    growth: transformValueDescriptionString(data?.values?.growth),
    followersPerDayAvg: transformValueDescriptionString(
      data?.values?.followers_per_day_avg
    ),
    newFollowersPerDay: {
      description: data?.values?.new_followers_per_day?.description,
      values: data?.values?.new_followers_per_day?.values?.map((v: any) => ({
        date: v?.date,
        value: v?.value,
      })),
    },
    followersPerDayAvgPrevious: transformValueDescriptionString(
      data?.values?.followers_per_day_avg_previous
    ),
  },
});

export const transformTopGenderAge = (data: any): TopGenderAge => ({
  title: data?.title,
  description: data?.description,
  values: {
    topGender: transformValueDescriptionString(data?.values?.top_gender),
    topAge: transformValueDescriptionString(data?.values?.top_age),
    genderAgePercentages: {
      description: data?.values?.gender_age_percentages?.description,
      values: {
        F: data?.values?.gender_age_percentages?.values?.F,
        M: data?.values?.gender_age_percentages?.values?.M,
        U: data?.values?.gender_age_percentages?.values?.U,
      },
    },
  },
});

export const transformTopCities = (data: any): TopCities => ({
  title: data?.title,
  description: data?.description,
  value: data?.value,
});

export const transformEngagementPosts = (data: any): EngagementPosts => ({
  title: data?.title,
  description: data?.description,
  values: {
    commentsAvg: transformValueDescriptionString(data?.values?.comments_avg),
    likesAvg: transformValueDescriptionString(data?.values?.likes_avg),
    interactionsAvg: transformValueDescriptionString(
      data?.values?.interactions_avg
    ),
    impressionsAvg: transformValueDescriptionString(
      data?.values?.impressions_avg
    ),
    reachAvg: transformValueDescriptionString(data?.values?.reach_avg),
    engagementRate: transformValueDescriptionString(
      data?.values?.engagement_rate
    ),
    engagementRateAvg: transformValueDescriptionString(
      data?.values?.engagement_rate_avg
    ),
    likeCommentRatio100: transformValueDescriptionString(
      data?.values?.like_comment_ratio_100
    ),
    likesSpread: transformValueDescriptionString(data?.values?.likes_spread),
    engagementRatePrevious: transformValueDescriptionString(
      data?.values?.engagement_rate_previous
    ),
    engagementRateAvgPrevious: transformValueDescriptionString(
      data?.values?.engagement_rate_avg_previous
    ),
  },
});

export const transformEngagementAccount = (data: any): EngagementAccount => ({
  title: data?.title,
  description: data?.description,
  values: {
    avgEngagedAccountsPerDay: transformValueDescriptionString(
      data?.values?.avg_engaged_accounts_per_day
    ),
    totalEngagedAccounts: transformValueDescriptionString(
      data?.values?.total_engaged_accounts
    ),
  },
});

export const transformPostsFrequency = (data: any): PostsFrequency => ({
  title: data?.title,
  description: data?.description,
  value: data?.value,
});

export const transformValuePostStory = (data: any): ValuePostsStory => ({
  title: data?.title,
  description: data?.description,
  values: {
    post: transformValueDescriptionString(data?.values?.post),
    story: transformValueDescriptionString(data?.values?.story),
  },
});

export const transformFollowsUnfollows = (data: any): FollowsUnfollows => ({
  title: data?.title,
  description: data?.description,
  values: {
    unfollowCriticalTh: {
      value: data?.values?.unfollow_critical_th?.value,
      description: data?.values?.unfollow_critical_th?.description,
    },
    unfollowMoreThanFollow: {
      value: data?.values?.unfollow_more_than_follow?.value,
      description: data?.values?.unfollow_more_than_follow?.description,
    },
  },
});

export const transformTopMentions = (data: any): TopMentions => {
  return {
    title: data?.title,
    description: data?.description,
    value: data?.value.map((v: any) => ({
      username: v.username,
      followersCount: v.followers_count,
      numMentions: v.num_mentions,
    })),
  };
};

export const transformValueDescriptionType = (
  data: any
): ValueDescriptionType => ({
  value: data?.value,
  description: data?.description,
});

export const transformValueDescriptionString = (
  data: any
): ValueDescriptionString => ({
  value: data?.value,
  description: data?.description,
});
